'use client';

import clsx from 'clsx';
import { createPortal } from 'react-dom';
import SvgClose from '@themaven-net/raven-assets/Close';
import { useOverlayContext } from '@/context/OverlayContext';
import styles from '@/components/raven/Overlay/styles.module.css';
import {
	type FC,
	useState,
	useEffect,
	useCallback,
	type PropsWithChildren,
} from 'react';

export interface OverlayProps extends PropsWithChildren {
	className?: string;
	disableCloseButton?: boolean;
	disableTransition?: boolean;
	id?: string;
	onClose?: () => void;
	variant: string;
}

const OverlayBase: FC<OverlayProps> = ({
	children,
	className,
	disableCloseButton = false,
	disableTransition = false,
	id,
	onClose,
	variant,
}) => {
	const { closeVariant, isOpen, registerOverlay, unregisterOverlay } =
		useOverlayContext();
	const open = isOpen(variant, id);

	const handleCloseEvent = useCallback(() => {
		if (onClose) {
			onClose();
			return;
		}
		closeVariant(variant, id);
	}, [closeVariant, id, onClose, variant]);

	useEffect(() => {
		const handleEsc = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				handleCloseEvent();
			}
		};
		window.addEventListener('keydown', handleEsc);

		return () => {
			window.removeEventListener('keydown', handleEsc);
		};
	}, [handleCloseEvent, id, variant]);

	useEffect(() => {
		registerOverlay(variant, id, open);
		return () => unregisterOverlay(variant, id);
	}, [id, open, registerOverlay, unregisterOverlay, variant]);

	return (
		<div className={clsx(styles.overlayContainer, { disableTransition, open })}>
			<div className={clsx(styles.overlayBackdrop)} />
			<div className={clsx(className, styles.overlay)}>
				{!disableCloseButton && (
					<button
						className={styles.close}
						onClick={handleCloseEvent}
						type="button"
					>
						<SvgClose height={16} width={16} />
					</button>
				)}
				<div className={styles.overlayContent}>{children}</div>
			</div>
		</div>
	);
};

export const Overlay: FC<OverlayProps> = ({ children, ...props }) => {
	const [isClient, setIsClient] = useState(false);

	useEffect(() => {
		setIsClient(true);
	}, []);

	if (!isClient) {
		return null;
	}

	return createPortal(
		<OverlayBase {...props}>{children}</OverlayBase>,
		document.body,
	);
};
